/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext } from "react";
import { Save, Settings, Sliders, AlertCircle } from "lucide-react";
import { AdminContext } from "../../context/AdminContext";
import { toast } from "react-toastify";

const MatchingConfiguration = () => {
  const {
    serviceCategories,
    fetchServiceCategories,
    fetchMatchConfig,
    saveMatchConfig,
  } = useContext(AdminContext);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Global weights
  const [subcityWeight, setSubcityWeight] = useState(25);
  const [languagesWeight, setLanguagesWeight] = useState(20);
  const [experienceWeight, setExperienceWeight] = useState(15);
  const [ratingWeight, setRatingWeight] = useState(15);
  const [dynamicFieldsPool, setDynamicFieldsPool] = useState(25);

  // Field weights: { fieldId: weight }
  const [fieldWeights, setFieldWeights] = useState({});

  // Store original values for cancel functionality
  const [originalValues, setOriginalValues] = useState({
    subcityWeight: 25,
    languagesWeight: 20,
    experienceWeight: 15,
    ratingWeight: 15,
    dynamicFieldsPool: 25,
    fieldWeights: {},
  });

  // Matchable fields by category
  const [matchableFieldsByCategory, setMatchableFieldsByCategory] = useState(
    {},
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Fetch service categories if not already loaded
      if (!serviceCategories || serviceCategories.length === 0) {
        await fetchServiceCategories();
      }

      // Fetch match config using AdminContext method
      const config = await fetchMatchConfig();

      if (config) {
        setSubcityWeight(config.subcityWeight || 25);
        setLanguagesWeight(config.languagesWeight || 20);
        setExperienceWeight(config.experienceWeight || 15);
        setRatingWeight(config.ratingWeight || 15);
        setDynamicFieldsPool(config.dynamicFieldsPool || 25);

        // Build fieldWeights map from fieldWeights array
        const weightsMap = {};
        if (config.fieldWeights && Array.isArray(config.fieldWeights)) {
          config.fieldWeights.forEach((fw) => {
            weightsMap[fw.fieldId] = fw.weight;
          });
        }
        setFieldWeights(weightsMap);

        // Store original values for cancel functionality
        setOriginalValues({
          subcityWeight: config.subcityWeight || 25,
          languagesWeight: config.languagesWeight || 20,
          experienceWeight: config.experienceWeight || 15,
          ratingWeight: config.ratingWeight || 15,
          dynamicFieldsPool: config.dynamicFieldsPool || 25,
          fieldWeights: { ...weightsMap },
        });
      }
    } catch (error) {
      console.error("Failed to load matching config:", error);
      toast.error("Failed to load matching configuration");
    } finally {
      setLoading(false);
    }
  };

  // Group matchable fields by category
  useEffect(() => {
    if (serviceCategories && serviceCategories.length > 0) {
      const grouped = {};
      serviceCategories.forEach((category) => {
        // Backend returns 'fields', not 'categoryFields'
        const matchableFields = category.fields?.filter(
          (field) => field.isMatchable,
        );
        if (matchableFields && matchableFields.length > 0) {
          grouped[category.id] = {
            category,
            fields: matchableFields,
          };
        }
      });
      setMatchableFieldsByCategory(grouped);
    }
  }, [serviceCategories]);

  const handleSave = async () => {
    try {
      setSaving(true);

      // Build fieldWeights array for API
      const fieldWeightsArray = Object.entries(fieldWeights).map(
        ([fieldId, weight]) => ({
          fieldId,
          weight: parseInt(weight) || 0,
        }),
      );

      const payload = {
        subcityWeight: parseInt(subcityWeight),
        languagesWeight: parseInt(languagesWeight),
        experienceWeight: parseInt(experienceWeight),
        ratingWeight: parseInt(ratingWeight),
        dynamicFieldsPool: parseInt(dynamicFieldsPool),
        fieldWeights: fieldWeightsArray,
      };

      // Use AdminContext method to save config
      const result = await saveMatchConfig(payload);

      if (result.success) {
        setIsEditing(false);
        loadData(); // Reload to get updated data
      }
    } catch (error) {
      console.error("Failed to save matching config:", error);
      toast.error("Failed to save matching configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Restore original values
    setSubcityWeight(originalValues.subcityWeight);
    setLanguagesWeight(originalValues.languagesWeight);
    setExperienceWeight(originalValues.experienceWeight);
    setRatingWeight(originalValues.ratingWeight);
    setDynamicFieldsPool(originalValues.dynamicFieldsPool);
    setFieldWeights({ ...originalValues.fieldWeights });
    setIsEditing(false);
  };

  const totalGlobalWeight =
    parseInt(subcityWeight) +
    parseInt(languagesWeight) +
    parseInt(experienceWeight) +
    parseInt(ratingWeight) +
    parseInt(dynamicFieldsPool);

  const totalFieldWeights = Object.values(fieldWeights).reduce(
    (sum, w) => sum + parseInt(w || 0),
    0,
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-dark-muted">
            Loading configuration...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12">
      <div className="max-w-6xl mx-auto">
        {/* Page Header */}
        <div className="bg-card dark:bg-dark-surface rounded-t-3xl shadow-sm border border-gray-100 dark:border-dark-border p-6 sm:p-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
          <div className="flex items-center gap-4">
            <Settings size={24} className="text-primary" />
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-text dark:text-dark-text">
                Matching Configuration
              </h1>
              <p className="text-gray-500 dark:text-dark-muted mt-1 sm:mt-2 text-sm sm:text-base">
                Configure weights for caregiver-job matching algorithm
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="px-6 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-bold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white font-bold shadow-lg shadow-primary/20 dark:shadow-none transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={18} /> Save Configuration
                    </>
                  )}
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="px-6 py-2.5 rounded-xl bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border text-text dark:text-dark-text hover:border-primary hover:text-primary dark:hover:border-primary dark:hover:text-primary font-bold transition-all flex items-center gap-2"
              >
                <Settings size={18} /> Edit Configuration
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="bg-card dark:bg-dark-surface rounded-b-3xl shadow-sm border-x border-b border-gray-100 dark:border-dark-border p-6 sm:p-8 space-y-8">
          {/* Weight Summary */}
          <div className="bg-gradient-to-r from-primary/5 to-transparent border border-primary/20 dark:border-primary/10 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle
                className="text-primary flex-shrink-0 mt-0.5"
                size={20}
              />
              <div className="flex-1">
                <p className="text-sm font-semibold text-text dark:text-dark-text mb-1">
                  Weight Distribution
                </p>
                <p className="text-xs text-gray-600 dark:text-dark-muted">
                  Global weights total:{" "}
                  <span className="font-bold">{totalGlobalWeight}</span> (should
                  equal 100)
                  {totalGlobalWeight !== 100 && (
                    <span className="text-red-600 dark:text-red-400 ml-2">
                      ⚠ Weights should sum to 100
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-600 dark:text-dark-muted mt-1">
                  Dynamic Fields Pool:{" "}
                  <span className="font-bold">{dynamicFieldsPool}%</span> is
                  distributed <span className="font-bold">per category</span>
                </p>
                <p className="text-xs text-gray-500 dark:text-dark-muted mt-1 italic">
                  Each category's field weights should sum to{" "}
                  {dynamicFieldsPool}% independently
                </p>
              </div>
            </div>
          </div>

          {/* Global Weights Section */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-2 h-6 bg-primary rounded-full"></div>
              <h3 className="text-primary dark:text-primary font-semibold uppercase text-sm tracking-wider">
                Global Matching Weights
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Subcity Weight */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-text dark:text-dark-text">
                    Subcity Match
                  </label>
                  <span className="text-sm font-bold text-primary">
                    {subcityWeight}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={subcityWeight}
                  onChange={(e) => setSubcityWeight(e.target.value)}
                  disabled={!isEditing}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 dark:text-dark-muted">
                  Weight for matching caregiver and job subcity
                </p>
              </div>

              {/* Languages Weight */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-text dark:text-dark-text">
                    Languages Match
                  </label>
                  <span className="text-sm font-bold text-primary">
                    {languagesWeight}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={languagesWeight}
                  onChange={(e) => setLanguagesWeight(e.target.value)}
                  disabled={!isEditing}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 dark:text-dark-muted">
                  Weight for matching spoken languages
                </p>
              </div>

              {/* Experience Weight */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-text dark:text-dark-text">
                    Experience Level
                  </label>
                  <span className="text-sm font-bold text-primary">
                    {experienceWeight}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={experienceWeight}
                  onChange={(e) => setExperienceWeight(e.target.value)}
                  disabled={!isEditing}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 dark:text-dark-muted">
                  Weight for years of experience
                </p>
              </div>

              {/* Rating Weight */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-text dark:text-dark-text">
                    Rating Score
                  </label>
                  <span className="text-sm font-bold text-primary">
                    {ratingWeight}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={ratingWeight}
                  onChange={(e) => setRatingWeight(e.target.value)}
                  disabled={!isEditing}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 dark:text-dark-muted">
                  Weight for caregiver rating (1-5 stars)
                </p>
              </div>

              <div className="space-y-2 md:col-span-2">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-text dark:text-dark-text">
                    Dynamic Fields Pool (Per Category)
                  </label>
                  <span className="text-sm font-bold text-primary">
                    {dynamicFieldsPool}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={dynamicFieldsPool}
                  onChange={(e) => setDynamicFieldsPool(e.target.value)}
                  disabled={!isEditing}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 dark:text-dark-muted">
                  Weight pool allocated to category-specific fields. Each
                  category's field weights should sum to this value.
                </p>
              </div>
            </div>
          </div>

          {/* Category-Specific Field Weights */}
          {Object.keys(matchableFieldsByCategory).length > 0 ? (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-2 h-6 bg-primary rounded-full"></div>
                <h3 className="text-primary dark:text-primary font-semibold uppercase text-sm tracking-wider">
                  Category-Specific Field Weights
                </h3>
              </div>

              <div className="space-y-8">
                {Object.entries(matchableFieldsByCategory).map(
                  ([categoryId, { category, fields }]) => {
                    // Calculate total weight for this category
                    const categoryFieldWeights = fields.reduce(
                      (sum, field) =>
                        sum + parseInt(fieldWeights[field.id] || 0),
                      0,
                    );
                    const isValidWeight =
                      categoryFieldWeights === parseInt(dynamicFieldsPool);

                    return (
                      <div
                        key={categoryId}
                        className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-6 border border-gray-200 dark:border-dark-border"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                              style={{ backgroundColor: category.color }}
                            >
                              {category.icon || category.label.charAt(0)}
                            </div>
                            <div>
                              <h4 className="font-bold text-text dark:text-dark-text">
                                {category.label}
                              </h4>
                              <p className="text-xs text-gray-500 dark:text-dark-muted">
                                {fields.length} matchable field
                                {fields.length !== 1 ? "s" : ""}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-text dark:text-dark-text">
                              Total: {categoryFieldWeights}%
                            </p>
                            {isValidWeight ? (
                              <p className="text-xs text-emerald-600 dark:text-emerald-400">
                                ✓ Equals {dynamicFieldsPool}%
                              </p>
                            ) : (
                              <p className="text-xs text-yellow-600 dark:text-yellow-400">
                                ⚠ Should equal {dynamicFieldsPool}%
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {fields.map((field) => (
                            <div
                              key={field.id}
                              className="bg-white dark:bg-dark-surface rounded-lg p-4 border border-gray-200 dark:border-dark-border"
                            >
                              <div className="flex justify-between items-center mb-2">
                                <label className="text-sm font-medium text-text dark:text-dark-text">
                                  {field.label}
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={fieldWeights[field.id] || 0}
                                  onChange={(e) =>
                                    setFieldWeights({
                                      ...fieldWeights,
                                      [field.id]: e.target.value,
                                    })
                                  }
                                  disabled={!isEditing}
                                  className="w-20 px-3 py-1.5 text-sm font-bold text-primary bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary/20 outline-none text-center disabled:opacity-50 disabled:cursor-not-allowed"
                                />
                              </div>
                              <p className="text-xs text-gray-500 dark:text-dark-muted">
                                Field ID:{" "}
                                <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">
                                  {field.id}
                                </code>
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-dark-muted">
              <Sliders
                size={48}
                className="mx-auto mb-4 text-gray-300 dark:text-gray-600"
              />
              <p className="font-medium">No matchable fields configured</p>
              <p className="text-sm mt-1">
                Add matchable fields to service categories to configure weights
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MatchingConfiguration;
